import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAbility } from '../contexts/AbilityContext';
import { PermissionInput, AppSubject } from '../types/auth';
import { AppAction } from '@/types/shared-enums.generated';
import type { FeatureKey } from '../types/feature-keys.generated';
import type { CapKey, GlobalTipoCode } from '../lib/authz';

interface CanAccessProps {
  children: React.ReactNode;

  /** Rank mínimo (hierárquico) — espelha o gate equivalente do backend. */
  minRank?: number | GlobalTipoCode;
  /** Capability do modelo unificado — espelha o gate equivalente do backend. */
  caps?: CapKey | CapKey[];
  permissions?: PermissionInput | PermissionInput[];
  permissionMode?: 'all' | 'any';
  capabilities?: keyof import('../types/auth').UserCapabilities | Array<keyof import('../types/auth').UserCapabilities>;
  features?: FeatureKey | FeatureKey[];
  fallback?: React.ReactNode;
  invert?: boolean;
}

/**
 * Componente condicional que renderiza seus filhos apenas se o usuário
 * tiver o rank/capabilities/permissões necessários.
 * 
 * @example
 * // Por rank hierárquico
 * <CanAccess minRank="supervisor">
 *   <AdminButton />
 * </CanAccess>
 *
 * @example
 * // Por capability do modelo unificado
 * <CanAccess caps="can_manage_agent_monitoring">
 *   <AgentMonitoringSection />
 * </CanAccess>
 *
 * @example
 * // Por permissão
 * <CanAccess permissions={{ action: 'create', subject: 'Automation' }}>
 *   <CreateButton />
 * </CanAccess>
 * 
 * @example
 * // Por capacidade
 * <CanAccess capabilities="canManageAutomations">
 *   <ManageSection />
 * </CanAccess>
 * 
 * @example
 * // Com fallback
 * <CanAccess 
 *   permissions="read:Employee" 
 *   fallback={<span>Sem permissão</span>}
 * >
 *   <EmployeeList />
 * </CanAccess>
 * 
 * @example
 * // Invertido (mostra se NÃO tem acesso)
 * <CanAccess caps="can_manage_all" invert>
 *   <RegularUserMessage />
 * </CanAccess>
 */

const CanAccess: React.FC<CanAccessProps> = ({
  children,
  minRank,
  caps,
  permissions,
  permissionMode = 'all',
  capabilities,
  features,
  fallback = null,
  invert = false,
}) => {
  const { hasRank, hasCap, capabilities: userCapabilities, isFeatureEnabled } = useAuth();
  const { ability } = useAbility();

  let hasMinRankAccess = true;
  if (minRank !== undefined) {
    hasMinRankAccess = hasRank(minRank);
  }

  let hasCapsAccess = true;
  if (caps) {
    hasCapsAccess = hasCap(caps);
  }

  // Verificar permissões usando CASL quando possível
  let hasPermissionAccess = true;
  if (permissions) {
    const permissionArray = Array.isArray(permissions) ? permissions : [permissions];
    
    if (permissionMode === 'all') {
      hasPermissionAccess = permissionArray.every((permission) => {
        if (typeof permission === 'string') {
          const [action, subject] = permission.split(':') as [AppAction, AppSubject];
          return ability.can(action, subject);
        }
        return ability.can(permission.action, permission.subject);
      });
    } else {
      hasPermissionAccess = permissionArray.some((permission) => {
        if (typeof permission === 'string') {
          const [action, subject] = permission.split(':') as [AppAction, AppSubject];
          return ability.can(action, subject);
        }
        return ability.can(permission.action, permission.subject);
      });
    }
  }

  // Verificar capacidades
  let hasCapabilityAccess = true;
  if (capabilities && userCapabilities) {
    const capabilityArray = Array.isArray(capabilities) ? capabilities : [capabilities];
    hasCapabilityAccess = capabilityArray.every(
      (capability) => userCapabilities[capability] === true
    );
  }

  // Verificar feature flags
  let hasFeatureAccess = true;
  if (features) {
    const arr = Array.isArray(features) ? features : [features];
    hasFeatureAccess = arr.every((k) => isFeatureEnabled(k));
  }

  // Determinar se deve renderizar
  const shouldRender = hasMinRankAccess && hasCapsAccess && hasPermissionAccess && hasCapabilityAccess && hasFeatureAccess;
  const finalShouldRender = invert ? !shouldRender : shouldRender;

  if (finalShouldRender) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default CanAccess;

